import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { 
  Table, 
  FileSpreadsheet, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  Info,
  ShoppingCart,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

// Format VENTE Standard: REF DE VENTE | VENDU LE | PLATEFORME | MATCH | LIEU | VILLE | PAYS | DATE | CATEGORIE | QTY | RANG | PRIX | NOTE | NOM CLIENT | MAIL CLIENT
const VENTE_COLUMNS = ['ref_vente', 'date_operation', 'source', 'evenement', 'lieu', 'ville', 'pays', 'date_evenement', 'categorie', 'qty', 'rang', 'prix', 'note', 'nom_client', 'mail_client'];

// Format VENTE Alternatif (Spreadsheet): REF | MATCH | VILLE | PAYS | DATE EVENT | PRIX | CATEGORIE | QTY | RANG | DATE VENTE
const VENTE_COLUMNS_ALT = ['ref_vente', 'evenement', 'ville', 'pays', 'date_evenement', 'prix', 'categorie', 'qty', 'rang', 'date_operation'];

// Format ACHAT Standard: MATCH | CATEGORIE | E-MAIL DU COMPTE | PW DU COMPTE | SOURCE | REF DE VENTE | NOM CLIENT | MAIL CLIENT | PREUVE ENVOI
const ACHAT_COLUMNS = ['evenement', 'categorie', 'email_compte', 'pw_compte', 'source', 'ref_vente', 'nom_client', 'mail_client', 'preuve_envoi'];

// Format ACHAT CHERNY: Match_No | Category | Qty | E-MAIL | PW | SOURCE (6 colonnes)
const ACHAT_COLUMNS_CHERNY = ['match_no', 'categorie', 'qty', 'email_compte', 'pw_compte', 'source'];

// Labels pour l'affichage
const VENTE_LABELS = ['REF DE VENTE', 'VENDU LE', 'PLATEFORME', 'MATCH', 'LIEU', 'VILLE', 'PAYS', 'DATE', 'CATÉGORIE', 'QTY', 'RANG', 'PRIX', 'NOTE', 'NOM CLIENT', 'MAIL CLIENT'];
const VENTE_LABELS_ALT = ['RÉF', 'MATCH', 'VILLE', 'PAYS', 'DATE EVENT', 'PRIX', 'CATÉGORIE', 'QTY', 'RANG', 'DATE VENTE'];
const ACHAT_LABELS = ['MATCH', 'CATÉGORIE', 'E-MAIL DU COMPTE', 'PW DU COMPTE', 'SOURCE', 'REF DE VENTE', 'NOM CLIENT', 'MAIL CLIENT', 'PREUVE ENVOI'];
const ACHAT_LABELS_CHERNY = ['MATCH N°', 'CATÉGORIE', 'QTY', 'E-MAIL COMPTE', 'PW COMPTE', 'SOURCE'];

// Match number to full name mapping (will be populated from existing tickets)
const MATCH_NAMES = {
  5: 'M05 Denmark vs Slovenia',
  14: 'M14',
  19: 'M19 Argentina vs Algeria',
  22: 'M22',
  23: 'M23',
  24: 'M24',
  29: 'M29',
  33: 'M33',
  43: 'M43',
  45: 'M45',
  47: 'M47 Portugal vs Uzbekistan',
  51: 'M51',
  53: 'M53',
  56: 'M56',
  66: 'M66',
  70: 'M70',
  79: 'M79',
  86: 'M86',
  92: 'M92',
  104: 'M104',
};

// All available column types for manual mapping
const ALL_COLUMN_TYPES = [
  { key: 'ref_vente', label: 'RÉF VENTE' },
  { key: 'evenement', label: 'MATCH' },
  { key: 'match_no', label: 'MATCH N°' },
  { key: 'lieu', label: 'LIEU' },
  { key: 'ville', label: 'VILLE' },
  { key: 'pays', label: 'PAYS' },
  { key: 'date_evenement', label: 'DATE EVENT' },
  { key: 'date_operation', label: 'DATE VENTE' },
  { key: 'prix', label: 'PRIX' },
  { key: 'categorie', label: 'CATÉGORIE' },
  { key: 'qty', label: 'QTY' },
  { key: 'rang', label: 'RANG' },
  { key: 'source', label: 'PLATEFORME' },
  { key: 'note', label: 'NOTE' },
  { key: 'nom_client', label: 'NOM CLIENT' },
  { key: 'mail_client', label: 'MAIL CLIENT' },
  { key: 'email_compte', label: 'EMAIL COMPTE' },
  { key: 'pw_compte', label: 'PW COMPTE' },
  { key: 'ignore', label: '(IGNORER)' },
];

// Parse CSV/TSV handling quoted fields with newlines
const parseCSVLine = (text, delimiter = '\t') => {
  const rows = [];
  let currentRow = [];
  let currentCell = '';
  let insideQuotes = false;
  
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];
    
    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote
        currentCell += '"';
        i++;
      } else {
        // Toggle quote mode
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of cell
      currentRow.push(currentCell.trim());
      currentCell = '';
    } else if ((char === '\n' || char === '\r') && !insideQuotes) {
      // End of row (skip \r in \r\n)
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
      if (currentCell || currentRow.length > 0) {
        currentRow.push(currentCell.trim());
        if (currentRow.some(cell => cell)) { // Only add non-empty rows
          rows.push(currentRow);
        }
        currentRow = [];
        currentCell = '';
      }
    } else {
      currentCell += char;
    }
  }
  
  // Don't forget the last cell/row
  if (currentCell || currentRow.length > 0) {
    currentRow.push(currentCell.trim());
    if (currentRow.some(cell => cell)) {
      rows.push(currentRow);
    }
  }
  
  return rows;
};

// Parse spreadsheet data into ticket objects
const parseSpreadsheetData = (text, type, customMapping = null) => {
  if (!text.trim()) return [];
  
  // Detect delimiter (tab for Excel/Sheets copy, or comma/semicolon for CSV)
  const firstLine = text.split('\n')[0];
  let delimiter = '\t';
  if (!firstLine.includes('\t')) {
    delimiter = firstLine.includes(';') ? ';' : ',';
  }
  
  // Use smart CSV parser that handles quoted fields with newlines
  const lines = parseCSVLine(text, delimiter);
  
  if (lines.length < 1) return [];
  
  // Determine columns to use
  let columns;
  let isChernyFormat = false;
  
  if (customMapping) {
    columns = customMapping;
  } else if (type === 'ACHAT') {
    // Auto-detect ACHAT format: Standard (9 cols) vs CHERNY (6 cols)
    const numCols = Math.max(...lines.map(l => l.length));
    const firstDataRow = lines.find(row => row.some(cell => cell && cell.trim()));
    
    // CHERNY format: Match_No(number) | Category(1-3) | Qty(1) | Email | PW | Source
    // First col is a small number (match number), 3rd col contains @
    if (firstDataRow && numCols >= 5 && numCols <= 7) {
      const col0 = firstDataRow[0] || '';
      const col1 = firstDataRow[1] || '';
      const col3 = firstDataRow[3] || '';
      
      const isMatchNo = /^\d{1,3}$/.test(col0.trim()); // 1-3 digit number
      const isCategoryNo = /^[1-4]$/.test(col1.trim()); // 1, 2, 3 or 4
      const isEmail = col3.includes('@');
      
      if (isMatchNo && (isCategoryNo || isEmail)) {
        columns = ACHAT_COLUMNS_CHERNY;
        isChernyFormat = true;
        console.log('Detected CHERNY format for ACHAT');
      } else {
        columns = ACHAT_COLUMNS;
      }
    } else {
      columns = ACHAT_COLUMNS;
    }
  } else {
    // For VENTE: Auto-detect format based on column count and content
    const numCols = Math.max(...lines.map(l => l.length));
    const firstDataRow = lines.find(row => row.some(cell => cell && cell.trim()));
    
    // Check if first column looks like a reference (9-12 digit number)
    // and second column contains event name (has " vs " or "World Cup")
    if (firstDataRow && numCols <= 12) {
      const firstCol = firstDataRow[0] || '';
      const secondCol = firstDataRow[1] || '';
      
      const isAltFormat = /^\d{9,12}$/.test(firstCol.trim()) && 
        (/ vs /i.test(secondCol) || /world cup|match/i.test(secondCol));
      
      if (isAltFormat) {
        columns = VENTE_COLUMNS_ALT;
      } else {
        columns = VENTE_COLUMNS;
      }
    } else {
      columns = VENTE_COLUMNS;
    }
  }
  
  // Parse data rows
  const tickets = [];
  lines.forEach(row => {
    if (row.every(cell => !cell)) return; // Skip empty rows
    
    const ticket = {
      type: type,
      ref_achat: '',
      ref_vente: '',
      statut: '',
      evenement: '',
      date_evenement: '',
      date_operation: '',
      heure_evenement: '',
      lieu: '',
      ville: '',
      pays: '',
      categorie: '',
      block: '',
      rang: '',
      place: '',
      qty: 1,
      prix: 0,
      devise: 'EUR',
      total: 0,
      source: '',
      nom_client: '',
      mail_client: '',
      note: '',
      mode_livraison: '',
      email_compte: '',
      pw_compte: '',
      preuve_envoi: '',
    };
    
    columns.forEach((field, index) => {
      const value = row[index] || '';
      if (field === 'ignore') return; // Skip ignored columns
      
      // Handle match_no -> evenement conversion
      if (field === 'match_no') {
        const matchNo = parseInt(value.trim());
        if (matchNo) {
          // Try to get full match name, or use "M" + number
          const fullName = MATCH_NAMES[matchNo];
          ticket.evenement = fullName || `M${matchNo.toString().padStart(2, '0')}`;
        }
        return;
      }
      
      // Handle category number -> "CATEGORIE X" conversion
      if (field === 'categorie') {
        const catValue = value.trim();
        // If it's just a number (1, 2, 3), convert to "CATEGORIE X"
        if (/^[1-9]$/.test(catValue)) {
          ticket.categorie = `CATEGORIE ${catValue}`;
        } else if (/^cat/i.test(catValue)) {
          // Already has "Cat" prefix → extraire le numéro proprement
          const numMatch = catValue.match(/\d+/);
          ticket.categorie = numMatch ? `CATEGORIE ${numMatch[0]}` : catValue.toUpperCase();
        } else {
          ticket.categorie = catValue;
        }
        return;
      }
      
      if (field === 'qty') {
        ticket[field] = parseInt(value.replace(/\s/g, '')) || 1;
      } else if (field === 'prix' || field === 'total') {
        // Parse various price formats
        let cleanValue = value
          .replace(/\s/g, '')
          .replace(/[€$£]/g, '')
          .trim();
        
        const hasCommaBeforeDot = cleanValue.indexOf(',') < cleanValue.indexOf('.') && cleanValue.indexOf('.') !== -1;
        const hasCommaOnly = cleanValue.indexOf(',') !== -1 && cleanValue.indexOf('.') === -1;
        
        if (hasCommaBeforeDot) {
          cleanValue = cleanValue.replace(/,/g, '');
        } else if (hasCommaOnly) {
          cleanValue = cleanValue.replace(',', '.');
        }
        
        ticket[field] = parseFloat(cleanValue) || 0;
      } else if (field === 'date_evenement' || field === 'date_operation' || field === 'date_event') {
        // Convert ISO format to DD/MM/YY
        let dateValue = value;
        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          const year = isoMatch[1].slice(-2);
          const month = isoMatch[2];
          const day = isoMatch[3];
          dateValue = `${day}/${month}/${year}`;
        }
        ticket[field] = dateValue;
      } else {
        ticket[field] = value;
      }
    });
    
    // Auto-calculate total if needed
    if (!ticket.total && ticket.prix && ticket.qty) {
      ticket.total = ticket.prix * ticket.qty;
    }
    
    // Set "ATTENTE D'INFO" for missing fields based on type
    if (type === 'ACHAT') {
      if (!ticket.evenement || ticket.evenement.trim() === '') {
        ticket.evenement = 'ATTENTE D\'INFO';
      }
      if (!ticket.categorie || ticket.categorie.trim() === '') {
        ticket.categorie = 'ATTENTE D\'INFO';
      }
      if (!ticket.email_compte || ticket.email_compte.trim() === '') {
        ticket.email_compte = 'ATTENTE D\'INFO';
      }
      if (!ticket.pw_compte || ticket.pw_compte.trim() === '') {
        ticket.pw_compte = 'ATTENTE D\'INFO';
      }
      if (!ticket.source || ticket.source.trim() === '') {
        ticket.source = 'ATTENTE D\'INFO';
      }
    } else {
      if (!ticket.ref_vente || ticket.ref_vente.trim() === '') {
        ticket.ref_vente = 'ATTENTE D\'INFO';
      }
      if (!ticket.evenement || ticket.evenement.trim() === '') {
        ticket.evenement = 'ATTENTE D\'INFO';
      }
      if (!ticket.source || ticket.source.trim() === '') {
        ticket.source = 'ATTENTE D\'INFO';
      }
      if (!ticket.categorie || ticket.categorie.trim() === '') {
        ticket.categorie = 'ATTENTE D\'INFO';
      }
    }
    
    // Only add if we have some meaningful data
    if (ticket.evenement || ticket.ref_vente || ticket.categorie || ticket.source || ticket.email_compte) {
      tickets.push(ticket);
    }
  });
  
  return tickets;
};

export const BulkImportModal = ({ open, onOpenChange, onImport }) => {
  const [pastedData, setPastedData] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [importType, setImportType] = useState('ACHAT'); // 'ACHAT' ou 'VENTE'
  const [columnMapping, setColumnMapping] = useState(null);
  const [showMappingEditor, setShowMappingEditor] = useState(false);
  const [matchNames, setMatchNames] = useState(MATCH_NAMES);

  // Load match names from API on mount
  useEffect(() => {
    const loadMatchNames = async () => {
      try {
        const { ticketService } = await import('../../services/api');
        const names = await ticketService.getMatchNames();
        if (names && Object.keys(names).length > 0) {
          setMatchNames(prev => ({ ...prev, ...names }));
          // Update global MATCH_NAMES for parsing
          Object.assign(MATCH_NAMES, names);
        }
      } catch (e) {
        console.log('Could not load match names:', e);
      }
    };
    if (open) {
      loadMatchNames();
    }
  }, [open]);

  // Memoized parse function to avoid re-renders
  const parseData = useCallback((text, type) => {
    if (!text.trim()) return [];
    return parseSpreadsheetData(text, type);
  }, []);

  // Memoized preview data
  const preview = useMemo(() => {
    if (!pastedData.trim()) return [];
    return parseData(pastedData, importType).slice(0, 5);
  }, [pastedData, importType, parseData]);

  // Total count for import button
  const totalCount = useMemo(() => {
    if (!pastedData.trim()) return 0;
    return parseData(pastedData, importType).length;
  }, [pastedData, importType, parseData]);

  const handlePaste = useCallback((e) => {
    const text = e.target.value;
    setPastedData(text);
    setResult(null);
  }, []);

  const handleTypeChange = useCallback((type) => {
    setImportType(type);
    setResult(null);
  }, []);

  const handleImport = useCallback(async (e) => {
    // Stop event propagation to prevent dialog from closing
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!pastedData.trim()) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const tickets = parseData(pastedData, importType);
      
      if (tickets.length === 0) {
        setResult({
          success: false,
          message: '❌ Aucune donnée valide trouvée.\n\nVérifiez que:\n• Les colonnes sont séparées par des tabulations\n• Le format correspond au type sélectionné (ACHAT/VENTE)',
        });
        setLoading(false);
        return;
      }
      
      // ========== VALIDATION DES DONNÉES ==========
      const validationErrors = [];
      
      if (importType === 'VENTE') {
        // Validation VENTE
        const ticketsWithoutRef = tickets.filter(t => !t.ref_vente || t.ref_vente.trim() === '');
        const ticketsWithoutMatch = tickets.filter(t => !t.evenement || t.evenement.trim() === '');
        const ticketsWithoutCategorie = tickets.filter(t => !t.categorie || t.categorie.trim() === '');
        const ticketsWithInvalidQty = tickets.filter(t => !t.qty || t.qty < 1 || t.qty > 100);
        const ticketsWithInvalidPrice = tickets.filter(t => t.prix < 0);
        
        if (ticketsWithoutRef.length > 0) {
          validationErrors.push(`• ${ticketsWithoutRef.length} ligne(s) sans RÉFÉRENCE DE VENTE`);
        }
        if (ticketsWithoutMatch.length > 0) {
          validationErrors.push(`• ${ticketsWithoutMatch.length} ligne(s) sans MATCH/ÉVÉNEMENT`);
        }
        if (ticketsWithoutCategorie.length > 0) {
          validationErrors.push(`• ${ticketsWithoutCategorie.length} ligne(s) sans CATÉGORIE`);
        }
        if (ticketsWithInvalidQty.length > 0) {
          validationErrors.push(`• ${ticketsWithInvalidQty.length} ligne(s) avec QTY invalide (doit être entre 1 et 100)`);
        }
        if (ticketsWithInvalidPrice.length > 0) {
          validationErrors.push(`• ${ticketsWithInvalidPrice.length} ligne(s) avec PRIX négatif`);
        }
        
        // Check for duplicate ref_vente
        const refs = tickets.map(t => t.ref_vente).filter(r => r);
        const duplicateRefs = refs.filter((r, i) => refs.indexOf(r) !== i);
        if (duplicateRefs.length > 0) {
          validationErrors.push(`• ${duplicateRefs.length} RÉFÉRENCE(S) EN DOUBLE détectée(s)`);
        }
        
      } else {
        // Validation ACHAT
        const ticketsWithoutMatch = tickets.filter(t => !t.evenement || t.evenement.trim() === '');
        const ticketsWithoutCategorie = tickets.filter(t => !t.categorie || t.categorie.trim() === '');
        const ticketsWithoutEmail = tickets.filter(t => !t.email_compte || t.email_compte.trim() === '');
        const ticketsWithInvalidEmail = tickets.filter(t => {
          if (!t.email_compte) return false;
          return !t.email_compte.includes('@') || !t.email_compte.includes('.');
        });
        const ticketsWithoutSource = tickets.filter(t => !t.source || t.source.trim() === '');
        
        if (ticketsWithoutMatch.length > 0) {
          validationErrors.push(`• ${ticketsWithoutMatch.length} ligne(s) sans MATCH/ÉVÉNEMENT`);
        }
        if (ticketsWithoutCategorie.length > 0) {
          validationErrors.push(`• ${ticketsWithoutCategorie.length} ligne(s) sans CATÉGORIE`);
        }
        if (ticketsWithoutEmail.length > 0) {
          validationErrors.push(`• ${ticketsWithoutEmail.length} ligne(s) sans E-MAIL DU COMPTE`);
        }
        if (ticketsWithInvalidEmail.length > 0) {
          validationErrors.push(`• ${ticketsWithInvalidEmail.length} ligne(s) avec E-MAIL INVALIDE (format incorrect)`);
        }
        if (ticketsWithoutSource.length > 0) {
          validationErrors.push(`• ${ticketsWithoutSource.length} ligne(s) sans SOURCE`);
        }
      }
      
      // Si erreurs critiques, bloquer l'import
      if (validationErrors.length > 0) {
        // Check if errors are critical (ref_vente for VENTE, match/categorie for both)
        const hasCriticalError = validationErrors.some(e => 
          e.includes('RÉFÉRENCE DE VENTE') || 
          e.includes('MATCH/ÉVÉNEMENT') || 
          e.includes('CATÉGORIE')
        );
        
        if (hasCriticalError) {
          setResult({
            success: false,
            message: `❌ Erreurs de validation:\n\n${validationErrors.join('\n')}\n\n⚠️ Corrigez ces erreurs avant d'importer.`,
          });
          setLoading(false);
          return;
        } else {
          // Non-critical warnings - allow import with warning
          toast.warning(`⚠️ Attention: ${validationErrors.length} avertissement(s) détecté(s)`, {
            duration: 5000,
          });
        }
      }
      // ========== FIN VALIDATION ==========
      
      const res = await onImport(tickets);
      
      // Show toast notification with correct field names
      const created = res.created || 0;
      const updated = res.updated || 0;
      const skipped = res.skipped || 0;
      
      let message = `✅ Import terminé: ${created} créé(s)`;
      if (updated > 0) message += `, ${updated} mis à jour`;
      if (skipped > 0) message += `, ${skipped} ignoré(s)`;
      
      toast.success(message, {
        duration: 4000,
      });
      
      // Close modal immediately after success
      setPastedData('');
      setResult(null);
      setLoading(false);
      onOpenChange(false);
      
    } catch (error) {
      setResult({
        success: false,
        message: error.message || 'Erreur lors de l\'import',
      });
      toast.error('Erreur lors de l\'import');
      setLoading(false);
    }
  }, [pastedData, importType, parseData, onImport, onOpenChange]);

  const handleClose = useCallback((e) => {
    // Stop propagation if event exists
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!loading) {
      setPastedData('');
      setResult(null);
      onOpenChange(false);
    }
  }, [loading, onOpenChange]);

  // Handle dialog open change
  const handleDialogOpenChange = useCallback((isOpen) => {
    if (!isOpen && !loading) {
      setPastedData('');
      setResult(null);
    }
    onOpenChange(isOpen);
  }, [loading, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Import depuis Feuille de Calcul
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Type Selection */}
          <div className="space-y-2">
            <Label className="text-white font-semibold">Type d'import</Label>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={importType === 'ACHAT' ? 'default' : 'outline'}
                onClick={() => handleTypeChange('ACHAT')}
                className={importType === 'ACHAT' 
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                  : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                ACHAT
              </Button>
              <Button
                type="button"
                variant={importType === 'VENTE' ? 'default' : 'outline'}
                onClick={() => handleTypeChange('VENTE')}
                className={importType === 'VENTE' 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'border-red-500/50 text-red-400 hover:bg-red-500/10'}
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                VENTE
              </Button>
            </div>
          </div>

          {/* Format info */}
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-200">
                <p className="font-medium mb-1">Format attendu pour {importType} :</p>
                {importType === 'ACHAT' ? (
                  <>
                    <p className="font-mono text-xs text-blue-300">
                      MATCH | CATÉGORIE | E-MAIL DU COMPTE | PW DU COMPTE | SOURCE
                    </p>
                    <p className="text-xs text-blue-400 mt-1">
                      Les champs REF DE VENTE, NOM CLIENT, MAIL CLIENT, PREUVE ENVOI sont remplis lors de l'attribution.
                    </p>
                    <p className="text-xs text-amber-400 mt-1">
                      ⚠️ Si une info manque, "ATTENTE D'INFO" sera affiché.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-mono text-xs text-blue-300 mb-2">
                      Format 1: REF | VENDU LE | PLATEFORME | MATCH | LIEU | VILLE | PAYS | DATE | CAT | QTY | RANG | PRIX
                    </p>
                    <p className="font-mono text-xs text-emerald-300 mb-2">
                      Format 2 (auto-détecté): REF | MATCH | VILLE | PAYS | DATE EVENT | PRIX | CAT | QTY | RANG | DATE VENTE
                    </p>
                    <p className="text-xs text-emerald-400 mt-1">
                      ✓ Le système détecte automatiquement le format. Les dates ISO (2026-06-24T18:00) sont converties en DD/MM/YY.
                    </p>
                    <p className="text-xs text-emerald-400">
                      ✓ Les prix €5,000.00 ou 5000€ sont parsés automatiquement.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Paste area */}
          <div className="space-y-2">
            <Label htmlFor="paste-data">Données copiées (sans en-têtes)</Label>
            <Textarea
              id="paste-data"
              data-testid="bulk-import-textarea"
              placeholder={importType === 'ACHAT' 
                ? "Collez vos lignes ACHAT ici...\nExemple: M34 ECUADOR VS CURA	CATEGORIE 2	email@icloud.com	password	BROCK"
                : "Collez vos lignes VENTE ici...\nExemple: 634822	02/02/26	VIAGOGO	M34 MATCH	STADE	MIAMI	USA	03/07/26	CAT 2	2	966€	note	NOM	email@client.com"}
              value={pastedData}
              onChange={handlePaste}
              className="min-h-[200px] bg-background border-border/50 font-mono text-xs"
            />
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-emerald-400">Aperçu ({preview.length} ligne(s) sur {totalCount})</Label>
                {columnMapping && (
                  <span className="text-xs text-zinc-400 bg-zinc-800 px-2 py-1 rounded">
                    Colonnes détectées: {columnMapping.slice(0, 5).join(' → ')}...
                  </span>
                )}
              </div>
              <div className="overflow-x-auto border border-border/50 rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-card">
                      {importType === 'ACHAT' ? (
                        <>
                          <th className="px-2 py-1 text-left text-muted-foreground">MATCH</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">CATÉGORIE</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">E-MAIL</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">PW</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">SOURCE</th>
                        </>
                      ) : (
                        <>
                          <th className="px-2 py-1 text-left text-muted-foreground">REF</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">MATCH</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">CATÉGORIE</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">PLATEFORME</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">PRIX</th>
                          <th className="px-2 py-1 text-left text-muted-foreground">CLIENT</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((ticket, i) => (
                      <tr key={i} className="border-t border-border/30">
                        {importType === 'ACHAT' ? (
                          <>
                            <td className="px-2 py-1 max-w-[200px] truncate">{ticket.evenement || '-'}</td>
                            <td className="px-2 py-1">{ticket.categorie || '-'}</td>
                            <td className="px-2 py-1 max-w-[150px] truncate">{ticket.email_compte || '-'}</td>
                            <td className="px-2 py-1">{ticket.pw_compte ? '••••' : '-'}</td>
                            <td className="px-2 py-1">{ticket.source || '-'}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-2 py-1 font-mono">{ticket.ref_vente || '-'}</td>
                            <td className="px-2 py-1 max-w-[200px] truncate">{ticket.evenement || '-'}</td>
                            <td className="px-2 py-1">{ticket.categorie || '-'}</td>
                            <td className="px-2 py-1">{ticket.source || '-'}</td>
                            <td className="px-2 py-1">{ticket.prix ? `${ticket.prix}€` : '-'}</td>
                            <td className="px-2 py-1">{ticket.nom_client || '-'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              data-testid="bulk-import-result"
              className={`flex items-center gap-2 p-3 rounded-md ${
                result.success
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  : 'bg-red-500/10 text-red-400 border border-red-500/20'
              }`}
            >
              {result.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <span className="text-sm">{result.message}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleClose(e);
              }}
              disabled={loading}
              className="border-border/50"
            >
              Annuler
            </Button>
            <Button
              type="button"
              data-testid="bulk-import-button"
              onClick={(e) => {
                e.stopPropagation();
                handleImport(e);
              }}
              disabled={loading || !pastedData.trim() || preview.length === 0}
              className="bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Table className="h-4 w-4 mr-2" />
                  Importer {preview.length > 0 ? `(${totalCount} lignes)` : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportModal;
